import React, { useState, useRef, useEffect, useCallback } from "react";
import JsBarcode from "jsbarcode";
import { useSelector } from "react-redux";
import { getInventoryallproducts } from "../API  Functions/InventoryFunctions";
import { useNavigate } from "react-router-dom";
import { routes } from "../Routes";
import { Image } from "antd";
import hisabaklogo from "../assets/Logo/hisabakLogo.svg";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/language-switcher";

/* ─────────────────────────────────────────────
   Barcode value format:
   [7-digit product code] + [6-digit weight]
   weight 23.32 kg  →  023320   (int 3 digits + dec 2 digits + trailing 0)
───────────────────────────────────────────── */
const formatWeightDigits = (weight) => {
  const num = parseFloat(weight) || 0;
  return Math.round(num * 10000)
    .toString()
    .padStart(6, "0");
};

const buildBarcodeValue = (code, weight) => {
  const c = String(code).padStart(7, "0").slice(0, 7);
  return c + formatWeightDigits(weight);
};

/* ── Debounce hook ── */
const useDebounce = (val, ms) => {
  const [d, setD] = useState(val);
  useEffect(() => {
    const t = setTimeout(() => setD(val), ms);
    return () => clearTimeout(t);
  }, [val, ms]);
  return d;
};

/* ── Barcode helpers ── */
const generateBarcodeSVG = (value, options = {}) => {
  const svg = document.createElement("svg");
  JsBarcode(svg, value, {
    format: "CODE128",
    lineColor: "#000",
    height: options.height || 60,
    displayValue: options.displayValue !== undefined ? options.displayValue : true,
    fontSize: options.fontSize || 14,
    margin: 0,
  });
  return svg.outerHTML;
};

const printLabel35x24 = ({ barcodeValue, productName, calculatedPrice, weight }) => {
  const barcodeSVG = generateBarcodeSVG(barcodeValue, {
    height: 60,
    displayValue: true,
  });

  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8" />
      <title>Print Label</title>
      <style>
        @page {
          size: 35mm 24mm;
          margin: 0;
        }

        body {
          margin: 0;
          padding: 0;
          font-family: Arial, sans-serif;
        }

        .label {
          width: 35mm;
          height: 24mm;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 1mm;
          page-break-after: always;
        }

        .label:last-child {
          page-break-after: auto;
        }

        .label svg {
          width: 33mm;
          height: 10mm;
          display: block;
        }

        .product-name {
          font-size: 2mm;
          font-weight: bold;
          overflow: hidden;
          display: flex;
          align-items: flex-end;
          flex: 1;
          margin-bottom: 1mm;
          text-align: center;
          justify-content: center;
        }

        .bottom-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 33mm;
          margin-top: 1mm;
        }

        .weight {
          font-size: 2mm;
          color: #111;
        }

        .price {
          font-size: 2mm;
          color: #111;
        }
      </style>
    </head>
    <body>
      <div class="label">
        <div class="product-name">${productName}</div>
        ${barcodeSVG}
        <div class="bottom-row">
          <span class="weight">${weight} kg</span>
          <span class="price">EGP ${calculatedPrice}</span>
        </div>
      </div>
    </body>
    </html>
  `;

  const printWindow = window.open("", "_blank");
  printWindow.document.write(htmlContent);
  printWindow.document.close();

  printWindow.onload = () => {
    printWindow.print();
    printWindow.close();
  };
};

/* ════════════════════════════════════════════
   Main Page Component
════════════════════════════════════════════ */
export default function WeightBarcodePage() {
  const user = useSelector((s) => s.login?.user?.result);
  const storeId = user?.company_id;
  const userId = user?.user_id;
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);

  /* form */
  const [productCode, setProductCode] = useState("");
  const [weight, setWeight] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);

  /* search */
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debouncedSearch = useDebounce(searchQuery, 350);
  const navigate = useNavigate();

  /* label preview */
  const [labelData, setLabelData] = useState(null);
  const [codeError, setCodeError] = useState("");
  const [weightError, setWeightError] = useState("");

  /* refs */
  const dropdownRef = useRef(null);
  const searchWrapRef = useRef(null);
  const previewBarcodeRef = useRef(null);


   

  /* ── Search products ── */
  useEffect(() => {
    if (!debouncedSearch || debouncedSearch.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    (async () => {
      setSearchLoading(true);
      try {
        const data = await getInventoryallproducts(
          userId, storeId, undefined, undefined, debouncedSearch, 10, 0, undefined, true
        );
        if (data?.result[0]?.status_code == 200) {
          const products = data?.result[0]?.response || data?.result || [];
         const arr = (Array.isArray(products) ? products : []).filter(
  (p) => p.machine_barcode && String(p.machine_barcode).trim() !== ""
);
          setSearchResults(arr);
          setShowDropdown(arr.length > 0);
        } else {
          setSearchResults([]);
          setShowDropdown(false);
        }
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    })();
  }, [debouncedSearch, userId, storeId]);

  /* ── Auto-fetch product when 7 digits entered ── */
  useEffect(() => {
    if (productCode.length !== 7) {
      if (productCode.length > 0) setSelectedProduct(null);
      return;
    }
    (async () => {
      try {
        const data = await getInventoryallproducts(
          userId, storeId, undefined, undefined, productCode, 5, 0, undefined, true
        );
        if (data?.result[0]?.status_code == 200) {
          const products = data?.result[0]?.response || data?.result || [];
       const arr = (Array.isArray(products) ? products : []).filter(
  (p) => p.machine_barcode && String(p.machine_barcode).trim() !== ""
);
          const match =
            arr.find((p) => String(p.default_code || p.machine_barcode || "").trim() === productCode) ||
            arr[0];
          setSelectedProduct(match || null);
        }
      } catch {
        setSelectedProduct(null);
      }
    })();
  }, [productCode, userId, storeId]);

  /* ── Render preview barcode ── */
  useEffect(() => {
    if (labelData && previewBarcodeRef.current) {
      try {
        JsBarcode(previewBarcodeRef.current, labelData.barcodeValue, {
          format: "CODE128",
          lineColor: "#000",
          height: 48,
          displayValue: false,
          margin: 0,
        });
      } catch {}
    }
  }, [labelData]);

  /* ── Close dropdown on outside click ── */
  useEffect(() => {
    const h = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        searchWrapRef.current && !searchWrapRef.current.contains(e.target)
      ) setShowDropdown(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  /* ── Select product from dropdown ── */
  const handleSelectProduct = (p) => {
    setSelectedProduct(p);
    const code = p.default_code || p.machine_barcode || p.barcode || "";
    setProductCode(String(code).slice(0, 7));
    setSearchQuery(p.product_name || p.name || "");
    setShowDropdown(false);
    setCodeError("");
  };

  /* ── Generate label ── */
  const handleGenerate = useCallback((weight) => {
    let ok = true;
    if (!/^\d{7}$/.test(productCode)) {
      setCodeError("Must be exactly 7 digits");
      ok = false;
    } else setCodeError("");

    const w = parseFloat(weight);
    if (!weight || isNaN(w) || w <= 0) {
      setWeightError("Enter a valid weight");
      ok = false;
    } else setWeightError("");

    if (!ok) return;

    const barcodeValue = buildBarcodeValue(productCode, weight);
    const pricePerKg = parseFloat(selectedProduct?.current_price || 0);
    const calculatedPrice = (w * pricePerKg).toFixed(2);
    const productName =
      selectedProduct?.product_name || selectedProduct?.name || `Product ${productCode}`;

    setLabelData({
      barcodeValue,
      productName,
      productCode,
      weight: w.toFixed(3),
      pricePerKg: pricePerKg.toFixed(2),
      calculatedPrice,
      weightDigits: formatWeightDigits(weight),
    });
  }, [productCode, weight, selectedProduct]);

  /* ── Print ── */
  const handlePrint = () => {
    if (!labelData) return;
    printLabel35x24(labelData);

      setProductCode("");
  setWeight("");
  setSelectedProduct(null);
  setSearchQuery("");
  setSearchResults([]);
  setShowDropdown(false);
  setLabelData(null);
  setCodeError("");
  setWeightError("");
  };

if (!storeId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 max-w-md w-full text-center">
          <div className="text-5xl mb-4">🏪</div>

          <h2 className="text-xl font-bold text-gray-800 mb-2">
            Store Login Required
          </h2>

          <p className="text-sm text-gray-500 mb-6">
            Please login to a store to access the Weight Label Printer screen.
          </p>

          <button
            onClick={() => navigate(routes.login)}
            className="w-full py-3 rounded-xl bg-amber-400 hover:bg-amber-500 text-white font-semibold transition"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }
  /* ──────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-gray-50 ">
          <div className="relative flex items-center justify-between py-4 px-10 border-b border-gray-300">
        <div className="">
          <Image
            src={hisabaklogo}
            alt="hisabak"
            width={120}
            height={37}
            preview={false}
          />
        </div>
        <h1 className="text-xl font-semibold text-gray-800">
          {t("Weight Label Printer")}
        </h1>

        <div className="flex gap-x-2 items-center">
         <LanguageSwitcher setLoading={setLoading} />
         
          <div className="w-12" />
        </div>
       
      </div>
      <div className="max-w-6xl mx-auto py-10 px-4">

        {/* ── Page header ── */}
        <div className="mb-8">
          {/* <h1 className="text-2xl font-bold text-gray-800 tracking-tight">
            Weight Label Printer
          </h1> */}
          <p className="text-base text-gray-500 mt-1">
            Search or enter a product, set the weight, and print a 35×24 mm barcode label.
          </p>
        </div>

       <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-6">

          {/* ════════════ LEFT — inputs ════════════ */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col gap-5">

            {/* Search bar */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
                Search Product
              </label>
              <div className="relative" ref={searchWrapRef}>
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-lg select-none">
                  ⌕
                </span>
                <input
                  type="text"
                  className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-700 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"
                  placeholder="Type product name…"
                  value={searchQuery}
                  autoComplete="off"
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                />
                {searchLoading && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-gray-200 border-t-amber-400 animate-spin" />
                )}

                {/* Dropdown */}
                {showDropdown && searchResults.length > 0 && (
                  <ul
                    ref={dropdownRef}
                    className="absolute z-50 top-[calc(100%+6px)] left-0 right-0 bg-white border border-gray-100 rounded-xl shadow-xl max-h-52 overflow-y-auto"
                  >
                    {searchResults.map((p, i) => (
                      <li
                        key={p.id || i}
                        onMouseDown={() => handleSelectProduct(p)}
                        className="flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-amber-50 transition-colors"
                      >
                        <span className="text-sm text-gray-700 truncate max-w-[70%]">
                          {p.product_name || p.name}
                        </span>
                        <span className="text-xs font-mono text-amber-600 bg-amber-50 px-2 py-0.5 rounded-md ml-2 shrink-0">
                          {p.default_code || p.machine_barcode || "—"}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 text-xs text-gray-600">
              <div className="flex-1 h-px bg-gray-300" />
              <span>or enter manually</span>
              <div className="flex-1 h-px bg-gray-300" />
            </div>

            {/* Product code */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
                Product Code
                <span className="normal-case font-normal text-gray-600 ml-1.5">7 digits</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={7}
                placeholder="e.g. 1234567"
                value={productCode}
                onChange={(e) => {
                  setProductCode(e.target.value.replace(/\D/g, "").slice(0, 7));
                  setCodeError("");
                }}
                className={`w-full px-4 py-2.5 rounded-xl border font-mono text-sm text-gray-800 placeholder-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition ${
                  codeError ? "border-red-300" : "border-gray-200"
                }`}
              />
              {codeError && (
                <p className="text-xs text-red-400 mt-1">{codeError}</p>
              )}
              {selectedProduct && productCode.length === 7 && !codeError && (
                <p className="text-xs text-emerald-500 mt-1 flex items-center gap-1">
                  <span>✓</span>
                  {selectedProduct.product_name || selectedProduct.name}
                </p>
              )}
            </div>

            {/* Weight */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
                Weight
                <span className="normal-case font-normal text-gray-300 ml-1.5">kg</span>
              </label>
              <div className="relative">
               <input
  type="number"
  min="0"
  max="99.999"
  step="0.001"
  placeholder="e.g. 23.320"
  value={weight}
 onChange={(e) => {
  const value = e.target.value;

  // Allow only up to 3 digits after decimal
  if (!/^\d*\.?\d{0,3}$/.test(value)) {
    return;
  }

  if (value === "" || parseFloat(value) <= 99.999) {
    setWeight(value);
    setWeightError("");

    if (value === "") {
      setLabelData(null);
    } else {
      handleGenerate(value);
    }
  }
}}
  className={`w-full px-4 pr-12 py-2.5 rounded-xl border font-mono text-sm text-gray-800 placeholder-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition ${
    weightError ? "border-red-300" : "border-gray-200"
  }`}
/>
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-300 pointer-events-none">
                  kg
                </span>
              </div>
              {weightError && (
                <p className="text-xs text-red-400 mt-1">{weightError}</p>
              )}
              {weight && !weightError && (
                <p className="text-xs text-gray-400 mt-1 font-mono">
                  Encoded → <span className="text-gray-600 font-semibold">{formatWeightDigits(weight)}</span>
                </p>
              )}
            </div>

            {/* Generate button */}
            {/* <button
              onClick={handleGenerate}
              className="mt-auto w-full py-3 rounded-xl bg-amber-400 hover:bg-amber-500 active:scale-[0.98] text-white font-bold text-sm tracking-wide transition-all shadow-md shadow-amber-100"
            >
              Generate Label
            </button> */}
          </div>

          {/* ════════════ RIGHT — preview ════════════ */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest mb-5">
              Label Preview
            </p>

            {!labelData || !selectedProduct ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center text-gray-200 gap-3">
                <div className="text-6xl opacity-60">▦</div>
                <p className="text-sm text-gray-500">
                  Fill in the details and click<br />
                  <span className="font-semibold text-gray-400">Generate Label</span>
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-5">

                {/* ── Label mockup ── */}
                <div className="bg-white border-2 border-gray-200 rounded-lg shadow-lg overflow-hidden" style={{ width: "280px" }}>
                  <div className="flex flex-col items-center px-3 py-2 gap-0.5">

                    {/* Product name */}
                    <p className="text-[9px] font-bold text-gray-800 text-center leading-tight line-clamp-2 w-full">
                      {labelData.productName}
                    </p>

                    {/* Barcode */}
                    <svg ref={previewBarcodeRef} className="w-full" style={{ height: "52px" }} />

                    {/* Barcode number in red */}
                    <p className="text-[10px] font-bold text-red-500 tracking-widest font-mono">
                      {labelData.barcodeValue}
                    </p>

                    {/* Weight + Price bottom row */}
                    <div className="flex items-center justify-between w-full mt-0.5">
                      <span className="text-[8px] text-gray-400 font-mono">
                        {labelData.weight} kg
                      </span>
                      <span className="text-[9px] font-extrabold text-gray-900">
                        EGP {labelData.calculatedPrice}
                      </span>
                    </div>

                  </div>
                </div>

                {/* Details table */}
                <div className="w-full bg-gray-50 rounded-xl border border-gray-100 p-4 grid grid-cols-2 gap-y-2 gap-x-3 text-sm">
                  {[
                    ["Product", labelData.productName],
                    ["Code", labelData.productCode],
                    ["Weight", `${labelData.weight} kg`],
                    ["Price / kg", `EGP ${labelData.pricePerKg}`],
                    ["Barcode", labelData.barcodeValue],
                    ["Total", `EGP ${labelData.calculatedPrice}`],
                  ].map(([k, v]) => (
                    <React.Fragment key={k}>
                      <span className="text-xs text-gray-400 font-medium">{k}</span>
                      <span className={`text-xs font-semibold font-mono text-gray-700 truncate ${k === "Total" ? "text-emerald-600 text-sm" : ""}`}>
                        {v}
                      </span>
                    </React.Fragment>
                  ))}
                </div>

                {/* Print button */}
                <button
                  onClick={handlePrint}
                  className="w-full py-3 rounded-xl border-2 border-amber-400 text-amber-500 font-bold text-sm hover:bg-amber-50 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                >
                  <span>🖨</span> Print 35×24 mm Label
                </button>

              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}